import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSeo } from "@/lib/api";
import { PAGE_SEO } from "@/lib/pageSeo";
import { useSettings } from "@/context/SettingsContext";
import { organizationLd } from "@/lib/jsonld";

const FALLBACK = {
  site_name: "Dharmartha",
  site_url: "",
  title_suffix: " · Dharmartha · धर्मार्थ",
  default_title: "Dharmartha · धर्मार्थ — Knowledge Traditions of Bharat",
  default_description: "Explore the knowledge traditions of Bharat — kathas, mantras, suktas, puja vidhi, Vedic knowledge, stotras, deities, temples and festivals.",
  default_keywords: "Dharmartha, Sanatana Dharma, Hindu scriptures, mantras, stotras, kathas, puja vidhi, panchang, temples, deities, festivals",
  og_image: "https://static.prod-images.emergentagent.com/jobs/f79bd64e-30d1-4119-8097-970fcb1907b0/images/3b9ff026968056eaa47b7857ac489fc09e0d6b0f0a2f4adcb652b9e34bfa99c7.jpeg",
  pages: {},
};

// Per-page SEO. Merges (item/props) > admin per-path override > admin defaults.
// `path` should be the route path (e.g. "/kathas"); item detail pages pass explicit title/description.
export default function Seo({ title, description, keywords, image, path, type = "website", noindex = false, jsonLd }) {
  const { data: cfg = FALLBACK } = useQuery({ queryKey: ["seo-config"], queryFn: getSeo, staleTime: 5 * 60_000 });
  const { crawl_enabled } = useSettings();
  const location = useLocation();
  const routePath = path || location.pathname || "/";
  const cleanPath = routePath.replace(/\/$/, "") || "/";
  const pageCfg = (cfg.pages && (cfg.pages[routePath] || cfg.pages[cleanPath])) || {};
  // Central static per-route SEO (title/description/keywords) as a fallback.
  const staticCfg = PAGE_SEO[routePath] || PAGE_SEO[cleanPath] || {};

  const rawTitle = title || pageCfg.title || staticCfg.title;
  const fullTitle = rawTitle
    ? (staticCfg.title && rawTitle === staticCfg.title ? rawTitle : `${rawTitle}${cfg.title_suffix || ""}`)
    : cfg.default_title;
  const desc = description || pageCfg.description || staticCfg.description || cfg.default_description;
  const kw = keywords || pageCfg.keywords || staticCfg.keywords || cfg.default_keywords;
  const img = image || cfg.og_image;
  const origin = cfg.site_url || (typeof window !== "undefined" ? window.location.origin : "");
  const url = `${origin.replace(/\/$/, "")}${routePath}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {cfg.favicon && <link rel="icon" href={cfg.favicon} />}
      {cfg.favicon && <link rel="apple-touch-icon" href={cfg.favicon} />}
      <meta name="description" content={desc} />
      {kw && <meta name="keywords" content={kw} />}
      <meta name="robots" content={(noindex || crawl_enabled === false) ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <link rel="canonical" href={url} />
      <meta property="og:site_name" content={cfg.site_name || "Dharmartha"} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={img} />
      <meta property="og:url" content={url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      <script type="application/ld+json">{JSON.stringify(organizationLd())}</script>
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
